import React from "react";
import { useTranslation } from "react-i18next";
import { SecondaryButton } from "components/shared/general";

/**
 * The action row at the foot of a modal.
 *
 * Cancel sits at the modal's **leading** edge and the confirming action at its trailing edge,
 * rather than the two sitting side by side: a button that discards the form should not be
 * adjacent to the one that commits it, because there a mis-aim costs the work.
 *
 * `justify-between` is direction-aware by itself — the leading edge is the left under `dir=ltr`
 * and the right under `dir=rtl` — so there is no `start`/`end` here to get the wrong way round.
 *
 * `w-full` is load-bearing, not decoration. `AmerModal`'s footer slot is itself
 * `flex … justify-end` (see `.amer-pop-footer`), so this row arrives as a flex *item*: without
 * a width it shrinks to its contents and the whole thing gets pushed to the trailing edge,
 * putting Cancel right back beside Save. That is exactly what happened to the modals that pass
 * `footer=` rather than rendering the row inside the body.
 *
 * It also owns the Cancel label, `t("button.cancel")` («إلغاء»). A button that closes a dialog
 * never reads «لا» / "No": `no` answers a yes/no question, it is not a way out of a form.
 *
 * Props:
 * - `onCancel`   — the close handler. Omit to leave the leading edge empty (a modal with no
 *                  way out other than the header's ✕).
 * - `cancelLabel`— override the copy, for the rare dialog where "Cancel" is not the word.
 * - `center`     — optional middle slot: a step counter, a hint, a secondary link.
 * - `children`   — the confirming action(s), kept together at the trailing edge.
 */
const ModalActions = ({
  onCancel,
  cancelLabel,
  center = null,
  children,
  className = "",
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`w-full flex items-center justify-between gap-3 flex-wrap ${className}`}
    >
      {onCancel ? (
        <SecondaryButton onClick={onCancel} type="button">
          {cancelLabel ?? t("button.cancel")}
        </SecondaryButton>
      ) : (
        // Holds the leading edge so the confirming action stays pinned to the trailing one
        // instead of sliding across to fill the row.
        <span />
      )}

      {/* `order-3` + `w-full` below `sm` drops the counter onto its own centered line
          instead of leaving it to `flex-wrap`'s default reflow: with three items and not
          enough room, that put Cancel and the counter alone on line one (`justify-between`
          shoving the counter to the far trailing edge) and pushed Previous/Next onto a
          second line pinned to the *leading* edge — backwards from the confirming action's
          documented trailing-edge spot. Forcing the counter to wrap alone keeps Cancel and
          the buttons together on one line at every width. */}
      {center && (
        <div className="w-full text-center order-3 sm:order-none sm:w-auto">
          {center}
        </div>
      )}

      <div className="flex items-center gap-3">{children}</div>
    </div>
  );
};

export default ModalActions;
